import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { upsertSurvey } from '@/lib/admin/surveys-api';

const DEFAULT_QUESTION =
  "What's actually getting in the way of doing your best work right now?";

export default function NewSurvey() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState('First conversation');
  const [audience, setAudience] = useState<'all' | 'segment'>('all');
  const [question, setQuestion] = useState(DEFAULT_QUESTION);
  const [submitting, setSubmitting] = useState(false);

  async function onLaunch(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !question.trim()) {
      toast({ title: 'Add a title and an opening question.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const id = await upsertSurvey({
        pulseOrgId: orgId,
        name: title.trim(),
        description: question.trim(),
        northVision: null,
      });
      toast({ title: 'Survey created' });
      navigate(`/app/admin/surveys/${id}`);
    } catch (err) {
      toast({
        title: 'Could not create survey',
        description: (err as Error).message,
        variant: 'destructive',
      });
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold">Start a conversation</h2>
        <p className="text-sm text-muted-foreground">
          Charles will use this as the opening prompt. You can refine anchors and follow-ups after launch.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New survey</CardTitle>
          <CardDescription>Takes about a minute.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onLaunch} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. May check-in"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Audience</Label>
              <RadioGroup
                value={audience}
                onValueChange={(v) => setAudience(v as 'all' | 'segment')}
                className="grid gap-2"
              >
                <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer">
                  <RadioGroupItem value="all" id="aud-all" />
                  <div>
                    <div className="text-sm font-medium">All employees</div>
                    <div className="text-xs text-muted-foreground">Everyone in the org gets the prompt.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer opacity-60">
                  <RadioGroupItem value="segment" id="aud-seg" disabled />
                  <div>
                    <div className="text-sm font-medium">A specific segment</div>
                    <div className="text-xs text-muted-foreground">Pick after launch from the survey editor.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="question">Opening question</Label>
              <Textarea
                id="question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                required
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate('/app/admin/surveys')}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Launch
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
